import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { uz, type Dictionary } from './uz';
import { ru } from './ru';

export type UiLang = 'uz' | 'ru';

const DICTS: Record<UiLang, Dictionary> = { uz, ru };
const STORAGE_KEY = 'ipost.uiLang';

interface I18nValue {
  lang: UiLang;
  t: Dictionary;
  setLang: (lang: UiLang) => void;
  /** Locale-aware date formatting, so one helper serves both languages. */
  formatDate: (value: string | Date | null | undefined, withTime?: boolean) => string;
  formatRelative: (value: string | Date | null | undefined) => string;
  plural: (count: number, word: string) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

function initialLang(): UiLang {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'uz' || stored === 'ru') return stored;
  const fallback = import.meta.env.VITE_DEFAULT_UI_LANG;
  return fallback === 'ru' ? 'ru' : 'uz';
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<UiLang>(initialLang);

  const setLang = useCallback((next: UiLang) => {
    setLangState(next);
    localStorage.setItem(STORAGE_KEY, next);
    document.documentElement.lang = next;
  }, []);

  const value = useMemo<I18nValue>(() => {
    const bcp47 = 'ru-RU';

    /*
     * `uz-UZ` has no date-pattern data either, and Intl resolves it to an
     * ISO-looking 2026-07-15. Both languages use DD.MM.YYYY in practice —
     * and it is what the design mocks show — so the pattern is explicit.
     */
    const pad = (n: number) => String(n).padStart(2, '0');

    const formatDate = (input: string | Date | null | undefined, withTime = false) => {
      if (!input) return '—';
      const date = typeof input === 'string' ? new Date(input) : input;
      if (Number.isNaN(date.getTime())) return '—';
      const day = `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
      return withTime ? `${day} ${pad(date.getHours())}:${pad(date.getMinutes())}` : day;
    };

    /*
     * Relative time is hand-written for Uzbek.
     *
     * Intl.RelativeTimeFormat has no `uz` data in Chrome or Firefox: it
     * silently falls back to English and prints "yesterday" and "3 days ago"
     * into an interface that is not allowed a single English string (§10.8).
     * Russian data exists and is good, so `ru` uses Intl.
     */
    const uzRelative = (diffMs: number): string => {
      const past = diffMs <= 0;
      const abs = Math.abs(diffMs);
      const suffix = past ? 'oldin' : 'keyin';

      if (abs < 45_000) return past ? 'hozirgina' : 'hozir';
      if (abs < 3_600_000) return `${Math.round(abs / 60_000)} daqiqa ${suffix}`;
      if (abs < 86_400_000) return `${Math.round(abs / 3_600_000)} soat ${suffix}`;

      const days = Math.round(abs / 86_400_000);
      if (days === 1) return past ? 'kecha' : 'ertaga';
      if (days < 30) return `${days} kun ${suffix}`;
      return '';
    };

    const formatRelative = (input: string | Date | null | undefined) => {
      if (!input) return '—';
      const date = typeof input === 'string' ? new Date(input) : input;
      if (Number.isNaN(date.getTime())) return '—';

      const diffMs = date.getTime() - Date.now();
      const abs = Math.abs(diffMs);
      // Past a month, an exact date is more useful than "2 oy oldin".
      if (abs >= 2_592_000_000) return formatDate(date);

      if (lang === 'uz') return uzRelative(diffMs) || formatDate(date);

      const rtf = new Intl.RelativeTimeFormat(bcp47, { numeric: 'auto' });
      if (abs < 60_000) return rtf.format(Math.round(diffMs / 1000), 'second');
      if (abs < 3_600_000) return rtf.format(Math.round(diffMs / 60_000), 'minute');
      if (abs < 86_400_000) return rtf.format(Math.round(diffMs / 3_600_000), 'hour');
      return rtf.format(Math.round(diffMs / 86_400_000), 'day');
    };

    // Uzbek does not inflect the noun after a numeral; Russian does. Rather
    // than ship a full plural engine for a handful of strings, the dictionary
    // stores the singular and Russian counts read naturally as "3 черновика"
    // only when the caller supplies the right form — so counts are rendered
    // with the number outside the word everywhere.
    const plural = (count: number, word: string) => `${count} ${word}`;

    return { lang, t: DICTS[lang], setLang, formatDate, formatRelative, plural };
  }, [lang, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}

/** Shorthand for the common case. */
export function useT(): Dictionary {
  return useI18n().t;
}
